(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const initialized = new WeakSet();
  let revealObserver;
  let wordObserver;
  let frame = 0;

  const buildObservers = () => {
    if (!('IntersectionObserver' in window)) return;
    revealObserver ||= new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: .1, rootMargin: '0px 0px -6% 0px' });
    wordObserver ||= new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          wordObserver.unobserve(entry.target);
        }
      });
    }, { threshold: .1 });
  };

  const initReveals = (root) => {
    buildObservers();
    root.querySelectorAll('.pw-reveal:not([data-pw-ready])').forEach((element) => {
      element.dataset.pwReady = 'true';
      if (reducedMotion.matches || !revealObserver) element.classList.add('is-visible');
      else revealObserver.observe(element);
    });
    root.querySelectorAll('.pw-blur-text:not([data-pw-ready])').forEach((element) => {
      element.dataset.pwReady = 'true';
      if (reducedMotion.matches || !wordObserver) element.classList.add('is-visible');
      else wordObserver.observe(element);
    });
  };

  const initHeader = () => {
    const header = document.querySelector('.section-header');
    const button = document.querySelector('[data-pw-menu-toggle]');
    const overlay = document.querySelector('[data-pw-menu-overlay]');
    if (!header) return;

    const updateHeader = () => header.classList.toggle('is-pw-scrolled', window.scrollY > 40);
    updateHeader();
    window.addEventListener('scroll', updateHeader, { passive: true });

    if (!button || !overlay || initialized.has(button)) return;
    initialized.add(button);
    const close = () => {
      button.setAttribute('aria-expanded', 'false');
      overlay.setAttribute('aria-hidden', 'true');
      overlay.classList.remove('is-open');
      document.body.classList.remove('pw-menu-open');
    };
    const open = () => {
      button.setAttribute('aria-expanded', 'true');
      overlay.setAttribute('aria-hidden', 'false');
      overlay.classList.add('is-open');
      document.body.classList.add('pw-menu-open');
      requestAnimationFrame(() => overlay.querySelector('a')?.focus({ preventScroll: true }));
    };
    button.addEventListener('click', () => button.getAttribute('aria-expanded') === 'true' ? close() : open());
    overlay.querySelectorAll('a').forEach((link) => link.addEventListener('click', close));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && overlay.classList.contains('is-open')) {
        close();
        button.focus();
      }
    });
  };

  const initHero = (root) => {
    root.querySelectorAll('[data-pw-hero]').forEach((hero) => {
      if (initialized.has(hero)) return;
      initialized.add(hero);
      requestAnimationFrame(() => setTimeout(() => hero.classList.add('is-mounted'), 100));
    });
  };

  const initCollections = (root) => {
    root.querySelectorAll('[data-pw-collections]').forEach((section) => {
      if (initialized.has(section)) return;
      initialized.add(section);
      const triggers = [...section.querySelectorAll('[data-pw-collection-trigger]')];
      const previews = [...section.querySelectorAll('[data-pw-collection-preview]')];
      const select = (index) => {
        triggers.forEach((trigger, i) => {
          trigger.classList.toggle('is-active', i === index);
          trigger.setAttribute('aria-pressed', i === index ? 'true' : 'false');
        });
        previews.forEach((preview, i) => preview.classList.toggle('is-active', i === index));
      };
      triggers.forEach((trigger, index) => {
        trigger.addEventListener('mouseenter', () => select(index));
        trigger.addEventListener('focus', () => select(index));
        trigger.addEventListener('click', () => {
          const url = trigger.dataset.pwCollectionUrl;
          if (url) window.location.href = url;
        });
      });
    });
  };

  const initReviews = (root) => {
    root.querySelectorAll('[data-pw-reviews]').forEach((section) => {
      if (initialized.has(section)) return;
      initialized.add(section);
      const cards = [...section.querySelectorAll('[data-pw-review]')];
      const button = section.querySelector('[data-pw-review-more]');
      let visible = 6;
      const render = () => {
        cards.forEach((card, index) => { card.hidden = index >= visible; });
        if (button) button.hidden = visible >= cards.length;
      };
      button?.addEventListener('click', () => { visible += 6; render(); });
      render();
    });
  };

  const initFadingVideo = (root) => {
    root.querySelectorAll('.pw-hero__video, .pw-closing__video').forEach((video) => {
      if (initialized.has(video)) return;
      initialized.add(video);
      video.loop = true;
      video.muted = true;
      video.playsInline = true;
      const reveal = () => requestAnimationFrame(() => video.classList.add('is-ready'));
      video.addEventListener('loadeddata', reveal);
      video.addEventListener('playing', reveal);
      if (video.readyState >= 2) reveal();
      if (video.paused) video.play().catch(() => {});
    });
  };

  const updateMotion = () => {
    frame = 0;
    if (reducedMotion.matches) return;
    const viewport = window.innerHeight || 1;
    document.querySelectorAll('.pw-craft__image').forEach((image) => {
      const rect = image.parentElement.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > viewport) return;
      const progress = (viewport - rect.top) / (viewport + rect.height);
      const amplitude = window.innerWidth < 750 ? 10 : 30;
      image.style.setProperty('--pw-parallax-y', `${(progress - .5) * amplitude}px`);
    });
    if (window.innerWidth >= 750) {
      document.querySelectorAll('[data-pw-stack-card]').forEach((card) => {
        const rect = card.getBoundingClientRect();
        const distance = Math.max(0, 108 - rect.top);
        const depth = Math.min(1, distance / Math.max(1, rect.height));
        card.style.setProperty('--pw-stack-scale', (1 - depth * .04).toFixed(3));
        card.style.setProperty('--pw-stack-opacity', (1 - depth * .35).toFixed(3));
      });
    }
  };

  const scheduleMotion = () => {
    if (!frame) frame = requestAnimationFrame(updateMotion);
  };

  const init = (root = document) => {
    initReveals(root);
    initHero(root);
    initCollections(root);
    initReviews(root);
    initFadingVideo(root);
    scheduleMotion();
  };

  document.addEventListener('DOMContentLoaded', () => {
    initHeader();
    init();
    window.addEventListener('scroll', scheduleMotion, { passive: true });
    window.addEventListener('resize', scheduleMotion, { passive: true });
  });
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
